import { Artifact } from '@/data/museum-data';

const RANGER_CURATORIAL_PRIORITY_IDS = [
  '97', '105', '13', '291', '292', '328', '266', '35', '36', '94', '127', '72',
  '38', '40', '41', '149', '42', '71', '168', '73', '232', '268', '278', '276',
  '101', '315', '110', '112', '113', '106', '186', '200', '79', '82',
  '329', '102', '235', '233', '234', '236', '255', '327', '126', '206', '207',
  '267', '270', '269', '274', '275', '70', '108', '154', '190', '314', '39',
  '277', '120', '111', '95', '88', '114', '326', '313', '160', '84', '67',
  '76', '155', '83', '161', '85', '93', '86', '87', '238', '104',
] as const;

const rangerPriorityById = new Map<string, number>(
  RANGER_CURATORIAL_PRIORITY_IDS.map((id, index) => [id, index]),
);

const getApproximateSortYear = (era: string): number => {
  const match = era.match(/\b(1[5-9]\d{2}|20\d{2})\b/);
  if (!match) return 9999;
  return Number(match[1]);
};

const scoreRangerArtifact = (artifact: Artifact): number => {
  const text = [
    artifact.objectTitle,
    artifact.objectType,
    artifact.collectionCategory,
    artifact.manufacturerOrMaker ?? '',
    artifact.modelOrDescription ?? '',
    artifact.historicalSignificance,
    artifact.provenanceNotes ?? '',
    artifact.approximateDateOrEra,
  ].join(' ').toLowerCase();

  let score = 0;
  score += artifact.objectType === 'firearm' ? 500 : 0;
  score += artifact.objectType === 'badge' ? 480 : 0;
  score += artifact.objectType === 'memorabilia' ? 220 : 0;
  score += artifact.objectType === 'document' || artifact.objectType === 'book' ? 140 : 0;
  score += artifact.objectType === 'photograph' ? 100 : 0;

  const year = getApproximateSortYear(artifact.approximateDateOrEra);
  if (year < 1900) score += 420;
  else if (year < 1935) score += 300;
  else if (year < 1975) score += 180;
  else if (year < 2000) score += 80;

  if (text.includes('duty-carried') || text.includes('carried by')) score += 260;
  if (text.includes('captain') || text.includes('senior captain')) score += 190;
  if (text.includes('presentation')) score += 120;
  if (text.includes('badge')) score += 120;
  if (text.includes('cinco peso') || text.includes('bottlecap')) score += 100;
  if (text.includes('pair') || text.includes('matched')) score += 90;
  if (text.includes('family') || text.includes('estate')) score += 80;
  if (text.includes('john r. hughes') || text.includes('bill mcdonald')) score += 320;
  if (text.includes('gonzaullas') || text.includes('joaquin jackson')) score += 260;
  if (text.includes('clint peoples') || text.includes('crowder') || text.includes('krumnow')) score += 160;
  if (text.includes('commemorative') || text.includes('anniversary')) score -= 220;
  if (text.includes('contemporary') || text.includes('reissue')) score -= 160;

  return score;
};

// Storage key for persisted exclusions
const EXCLUSIONS_STORAGE_KEY = 'yo-ranch-excluded-artifacts';

// Default exclusions (can be overridden by localStorage)
// Duplicates: 139=6 (Winchester 1894), 140=8 (Model 55), 141=4 (Model 1892), 142=12 (Barrett), 145=36 (Gonzaullas badges), 147=38/69 (Crowder), 148=72/94 (Jackson), 196=74 (Mitchell Colt 1911), 109=236 (Brakefield-Lucas SW), 179=236 (Brakefield-Lucas SW), 68=38 (Crowder .38 Super), 192=268 (Horgen Colt SAA), 194=265 (Cole Agee Colt SAA), 195=266 (Gonzaullas Winchester 1892), 272=265 (Cole Agee duplicate), 273=270 (Odom Colt duplicate), 151=70 (Joe Coleman Gold Cup pair duplicate)
// Additional: 201=263 (Colt 1855 duplicate with wrong photo), 202=105 (Winchester 1873 WGM duplicate), 204 (YO Kleinguenther - no photo), 205 (Cawthon Delta Elite - wrong photo)
const defaultExcludedIds = ['30', '33', '51', '53', '54', '55', '58', '59', '61', '62', '68', '69', '77', '78', '109', '115', '116', '117', '121', '122', '123', '134', '135', '139', '140', '141', '142', '145', '147', '148', '151', '162', '163', '164', '165', '166', '179', '184', '191', '192', '194', '195', '196', '198', '199', '201', '202', '204', '205', '215', '216', '217', '219', '224', '226', '272', '273', '282', '304', '305', '308', '309', '310', '312']; // Iver Johnson (30), Colt Cloverleaf (33), Crowder Combat Commander (69 - wrong photo), duplicates (191, 226 = Harris SAA pair, 199 = Horgen modern SAA duplicate), entries with wrong/missing photos, frontier duplicates: 53/121=17 (vintage spurs), 54=20 (Kerr arrowheads), 55/122=21 (Gillespie tools), 58=26 (working spurs), 59=27 (tooled spurs), 61/134/304=28 (centennial spurs), 62/135/305=29 (1983 spurs), 123=57 (silver-mounted spurs), 51/77=24 (bore gauge), 78=52 (padlock), 162=93 (flag), 163=85 (knife), 164=88 (Dean boots), firearms/ranger/art duplicates: 115=237 (Bowman Blackhawk), 116=238 (Bowman Vaquero pair), 117=239 (Bowman Old Army pair), 198=267 (Aycock Gold Cup), 215=247 (Uberti Buntline), 216=246 (S&W Model 10-6), 217=248 (Colt Detective Special), 219=249 (Springfield 1835), 224=263 (Colt 1855), art: 165=75 (Texian Bronze), 166=74 (Lone Rider), 184=114 (Straight Shooter Bronze), 308=74 (Lone Rider stub), 309=75 (Texian stub), 312=144 (Jay Banks S&W duplicate), 282=304 (Centennial gal-leg spurs duplicate), 310=16 (Black parade saddle duplicate)

/**
 * Get the list of excluded artifact IDs from localStorage or defaults
 */
export const getExcludedArtifactIds = (): string[] => {
  try {
    const stored = localStorage.getItem(EXCLUSIONS_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.error('Error reading exclusions from localStorage:', e);
  }
  return [...defaultExcludedIds];
};

/**
 * Add an artifact ID to the exclusion list
 */
export const addExcludedArtifact = (artifactId: string): void => {
  const current = getExcludedArtifactIds();
  if (!current.includes(artifactId)) {
    const updated = [...current, artifactId];
    localStorage.setItem(EXCLUSIONS_STORAGE_KEY, JSON.stringify(updated));
  }
};

/**
 * Remove an artifact ID from the exclusion list
 */
export const removeExcludedArtifact = (artifactId: string): void => {
  const current = getExcludedArtifactIds();
  const updated = current.filter(id => id !== artifactId);
  localStorage.setItem(EXCLUSIONS_STORAGE_KEY, JSON.stringify(updated));
};

// ---- Collection overrides (DB-backed, in-memory cache) -----------------------
// Admins can record overrides in the `artifact_collection_overrides` table to
// strip a specific collection from a specific artifact (e.g., remove a
// false-positive longhorn match). The page that renders collections calls
// `setCollectionOverrides` once on load with the rows fetched from Supabase;
// subsequent calls to `getArtifactCollections` apply them in memory.

export interface CollectionOverride {
  artifactId: string;
  collection: string;
  /**
   * - 'remove'   → strip `collection` from the artifact's resolved set
   * - 'reassign' → strip the original collection (stored in `replaces`) and
   *                add `collection` instead
   * - 'flag'     → no effect on resolved collections; used only by the
   *                admin review queue to record pending/confirmed states
   */
  action: 'remove' | 'reassign' | 'flag';
  /** Source collection for a reassignment (the one being replaced). */
  replaces?: string;
  /** pending | confirmed | removed | reassigned (mirrors DB column). */
  reviewStatus?: 'pending' | 'confirmed' | 'removed' | 'reassigned';
}

let collectionOverrides: CollectionOverride[] = [];

export const setCollectionOverrides = (overrides: CollectionOverride[]): void => {
  collectionOverrides = overrides;
};

export const getCollectionOverrides = (): CollectionOverride[] => collectionOverrides;

// ---- Rule predicates (single source of truth) ------------------------------
// Both `getArtifactCollections` (production resolver) AND the audit-page
// pipeline tracer import these. Edit a rule here and the audit UI updates
// automatically — no drift possible.

export interface CollectionRule {
  id: string;
  /** Collection this rule contributes to. */
  collection: 'firearms' | 'ranger' | 'frontier' | 'art' | 'longhorn';
  /** Human-readable description of the predicate, shown in the audit UI. */
  description: string;
  /** Predicate evaluated against the artifact. */
  test: (ctx: RuleContext) => boolean;
  /**
   * Optional literal regex used by the predicate. Exposed so the audit UI can
   * display the exact source string (including any negative lookahead) and
   * highlight the matched substring inside a snippet. `null` means the rule
   * is not regex-based (e.g. an objectType / category equality check).
   */
  regex: RegExp | null;
}

export interface RuleContext {
  artifact: Artifact;
  category: string;
  significance: string;
  provenance: string;
  title: string;
  model: string;
  context: string;
  /** Concatenated lower-cased haystack used by longhorn rules. */
  haystack: string;
}

export const buildRuleContext = (artifact: Artifact): RuleContext => {
  const category = artifact.collectionCategory.toLowerCase();
  const significance = (artifact.historicalSignificance || '').toLowerCase();
  const provenance = (artifact.provenanceNotes || '').toLowerCase();
  const title = (artifact.objectTitle || '').toLowerCase();
  const model = (artifact.modelOrDescription || '').toLowerCase();
  const context = (artifact.historicalContext || '').toLowerCase();
  const haystack = `${title} ${model} ${category} ${significance} ${provenance} ${context}`;
  return { artifact, category, significance, provenance, title, model, context, haystack };
};

export const COLLECTION_RULES: CollectionRule[] = [
  // Firearms ---------------------------------------------------------------
  {
    id: 'firearms-objectType-or-category',
    collection: 'firearms',
    description: "objectType='firearm' OR category includes 'historic firearms' / equals 'firearms'",
    regex: null,
    test: ({ artifact, category }) =>
      artifact.objectType === 'firearm' ||
      category.includes('historic firearms') ||
      category === 'firearms',
  },
  // Ranger -----------------------------------------------------------------
  {
    id: 'ranger-category-or-provenance',
    collection: 'ranger',
    description:
      "category includes 'ranger'/'law enforcement' OR significance/provenance mentions 'texas ranger' OR objectType='badge'",
    regex: null,
    test: ({ artifact, category, significance, provenance }) =>
      category.includes('ranger') ||
      category.includes('law enforcement') ||
      significance.includes('texas ranger') ||
      provenance.includes('texas ranger') ||
      artifact.objectType === 'badge',
  },
  // Frontier ---------------------------------------------------------------
  {
    id: 'frontier-heritage-signals',
    collection: 'frontier',
    description:
      "category includes frontier/spur/leather/memorabilia/centennial/heritage/y.o. ranch/ranch OR objectType in {spur, saddle, arrowhead, memorabilia, other}",
    regex: null,
    test: ({ artifact, category }) =>
      category.includes('frontier') ||
      category.includes('spur') ||
      category.includes('leather') ||
      category.includes('memorabilia') ||
      category.includes('centennial') ||
      category.includes('heritage') ||
      category.includes('y.o. ranch') ||
      category.includes('ranch') ||
      artifact.objectType === 'spur' ||
      artifact.objectType === 'saddle' ||
      artifact.objectType === 'arrowhead' ||
      artifact.objectType === 'memorabilia' ||
      artifact.objectType === 'other',
  },
  // Fine Art & Sculpture (merged into the Frontier collection — May 2026) ---
  // Previously surfaced as its own 'art' collection. Curators consolidated
  // it with "Spurs, Leather & Frontier Objects" so artworks now appear
  // under the frontier slug alongside working-life objects.
  {
    id: 'art-objectType-or-category',
    collection: 'frontier',
    description:
      "(merged into frontier) objectType='artwork' OR category includes 'fine art'/'bronze'/'sculpture'/'western art'",
    regex: null,
    test: ({ artifact, category }) =>
      artifact.objectType === 'artwork' ||
      category.includes('fine art') ||
      category.includes('bronze') ||
      category.includes('sculpture') ||
      category.includes('western art'),
  },
  // Longhorn (tightened Apr-2026) -----------------------------------------
  {
    id: 'longhorn-word',
    collection: 'longhorn',
    description: 'Regex /\\blonghorn\\b/ matches anywhere in the haystack',
    regex: /\blonghorn\b/i,
    test: ({ haystack }) => /\blonghorn\b/.test(haystack),
  },
  {
    id: 'tlbaa',
    collection: 'longhorn',
    description: 'Regex /\\btlbaa?\\b/ (TLBA or TLBAA acronym) matches the haystack',
    regex: /\btlbaa?\b/i,
    test: ({ haystack }) => /\btlbaa?\b/.test(haystack),
  },
  {
    id: 'texas-longhorn-breeders',
    collection: 'longhorn',
    description: "Phrase 'texas longhorn breeders' appears in the haystack",
    regex: /texas longhorn breeders/i,
    test: ({ haystack }) => /texas longhorn breeders/.test(haystack),
  },
];

const applyCollectionOverrides = (
  artifactId: string,
  collections: string[]
): string[] => {
  const relevant = collectionOverrides.filter((o) => o.artifactId === artifactId);
  if (relevant.length === 0) return collections;

  const removals = new Set<string>();
  const additions = new Set<string>();

  for (const o of relevant) {
    if (o.action === 'remove') {
      removals.add(o.collection);
    } else if (o.action === 'reassign') {
      if (o.replaces) removals.add(o.replaces);
      additions.add(o.collection);
    }
    // 'flag' does not affect live mappings.
  }

  const next = collections.filter((c) => !removals.has(c));
  for (const add of additions) {
    if (!next.includes(add)) next.push(add);
  }
  return next;
};

/**
 * Determines which collections an artifact belongs to based on its properties.
 * An artifact can belong to multiple collections simultaneously.
 */
export const getArtifactCollections = (artifact: Artifact): string[] => {
  // Skip excluded artifacts
  const excludedIds = getExcludedArtifactIds();
  if (excludedIds.includes(artifact.id)) {
    return [];
  }

  // Evaluate the shared rule predicates (same constants the audit page reads).
  const ctx = buildRuleContext(artifact);
  const matchedCollections: string[] = [];
  for (const rule of COLLECTION_RULES) {
    if (rule.test(ctx) && !matchedCollections.includes(rule.collection)) {
      matchedCollections.push(rule.collection);
    }
  }

  return applyCollectionOverrides(artifact.id, matchedCollections);
};

export const sortArtifactsForCollection = <T extends Artifact>(
  items: T[],
  collectionType: string,
): T[] => {
  if (collectionType !== 'ranger') return items;

  return [...items].sort((a, b) => {
    const priorityA = rangerPriorityById.get(a.id);
    const priorityB = rangerPriorityById.get(b.id);

    if (priorityA !== undefined || priorityB !== undefined) {
      return (priorityA ?? Number.MAX_SAFE_INTEGER) - (priorityB ?? Number.MAX_SAFE_INTEGER);
    }

    const scoreDiff = scoreRangerArtifact(b) - scoreRangerArtifact(a);
    if (scoreDiff !== 0) return scoreDiff;

    const yearDiff = getApproximateSortYear(a.approximateDateOrEra) - getApproximateSortYear(b.approximateDateOrEra);
    if (yearDiff !== 0) return yearDiff;

    return a.objectTitle.localeCompare(b.objectTitle);
  });
};

/**
 * Allowlist of phrases that suppress the Y.O. brand rule. Exported so the
 * admin audit page can render the active list and verify which phrases would
 * be scrubbed for a given artifact.
 */
export const YO_BRAND_ALLOWLIST: { pattern: RegExp; label: string }[] = [
  { pattern: /\by\.?o\.?\s+ranch\b/gi, label: 'Y.O. Ranch' },
  { pattern: /\by\.?o\.?\s+centennial\b/gi, label: 'Y.O. Centennial' },
  { pattern: /\by\.?o\.?\s+headquarters\b/gi, label: 'Y.O. Headquarters' },
  { pattern: /\by\.?o\.?\s+hotel\b/gi, label: 'Y.O. Hotel' },
  { pattern: /\by\.?o\.?\s+social club\b/gi, label: 'Y.O. Social Club' },
  { pattern: /\by\.?o\.?\s+trail\b/gi, label: 'Y.O. Trail' },
  { pattern: /\by\.?o\.?\s+saddlery\b/gi, label: 'Y.O. Saddlery' },
  { pattern: /\by\.?o\.?\s+invitational\b/gi, label: 'Y.O. Invitational' },
  { pattern: /\by\.?o\.?\s+sale\b/gi, label: 'Y.O. Sale' },
  {
    pattern: /\by\.?o\.?\s+brand of (?:hospitality|service|excellence)\b/gi,
    label: 'Y.O. brand of hospitality/service/excellence (figurative)',
  },
];

